/**
 * Create a new instance of the AddToCart component.
 *
 * @param {HTMLElement|false} [form=false] - The HTML form element containing the product information.
 * @param {HTMLElement|false} [qtySelector=false] - The HTML element used to select the quantity of the product to add to the cart.
 * @param {HTMLElement|false} [comboBox=false] - The HTML select element containing the product variant options.
 */

export class AddToCart {
  constructor(form = false, qtySelector = false, comboBox = false) {
    this.formEl = form;
    this.qtySelector = qtySelector;
    this.comboBox = comboBox;
    this.buttonEl = false;
    this.loading = false;

    this._init();
  }

  /**
   * Handles the click event when the "Add to Cart" button is clicked.
   * @returns {Promise<void>} - A Promise that resolves when the product is successfully added to the cart.
   */
  _handleClick = async () => {
    if (this.loading) {
      return;
    }

    // Set loading state and change button text
    this.loading = true;
    this.buttonEl.innerText = "Adding...";

    // Get product data
    const productData = this._getProductData(
      this.formEl,
      this.qtySelector,
      this.comboBox
    );
    if (!productData) {
      throw new Error("Product Data not found");
    }

    const cartData = {
      id: productData.id,
      quantity: productData.quantity,
    };
    if (productData.selling_plan) {
      cartData["selling_plan"] = productData.selling_plan;
    }

    // send product to data server
    const addRequest = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cartData),
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error("Something went wrong");
      })
      .then(() => {
        // Success: Product added to cart
        this.loading = false;
        this.buttonEl.innerText = "Add To Cart";
        gtag("event", "conversion", {
          send_to: "AW-11061188277/VBndCO-QnJYYELWtsZop",
        });
        console.log("gtag event sent successfully");
      })
      .catch((error) => {
        console.log("ERROR", error);
      });
  };

  /**
   * Gets the product data from the form, quantity selector, and combo box.
   * @param {HTMLElement} formEl - The form element containing the product information.
   * @param {HTMLElement} qtySelector - The element used to select the quantity of the product to add to the cart.
   * @param {HTMLElement} comboBox - The select element containing the product variant options.
   * @returns {Object|null} - An object containing the product data, or null if the data is not found.
   */
  _getProductData = (formEl, qtySelector, comboBox) => {
    const product = {
      id: formEl.dataset.productVariant || false,
      qty: qtySelector ? qtySelector._getTotal() : 1,
      selling_plan_id: comboBox ? comboBox._getSellingPlan() : false,
    };

    // Validate product data
    if (!product.id) {
      throw new Error("Product variant ID not found");
    }

    return {
      id: product.id,
      quantity: product.qty,
      selling_plan: product.selling_plan_id,
    };
  };

  /**
   * Initialize the AddToCart component by setting the button element, adding click and keydown event listeners, and calling the handleClick function when events occur.
   * @throws {Error} If the atc-button custom element is not found within the form element.
   * @returns {void}
   */
  _init = () => {
    this.buttonEl = this.formEl.querySelector("atc-button");

    if (!this.buttonEl) {
      throw new Error("atc-button custom element not found");
    }

    this.buttonEl.addEventListener("click", (e) => this._handleClick(e));
    this.buttonEl.addEventListener("keydown", (e) => {
      if (e.key == "Enter") {
        this._handleClick(e);
      }
    });
  };
}
